/** Utilidades para biblioteca de gráficos por dashboard e instancias. */

import { resolveImageDensityConfig } from './imageDensityConfig';
import { resolveGeoHeatmapConfig, migrateLegacyMapToGeoHeatmap } from './geoHeatmapConfig';
import { resolveImageCarouselConfig } from './imageCarouselConfig';
import { originalUsageCount } from './originalUsage';

export function enrichChartConfig(chart, data) {
  if (!chart) return chart;
  const migrated = migrateLegacyMapToGeoHeatmap(chart);
  return enrichChartImageConfig(migrated, data);
}

export function enrichChartImageConfig(chart, data) {
  if (!chart) return chart;

  if (
    chart.type === 'imageDensity' ||
    chart.imageDensityConfig ||
    chart.advancedConfig?.imageDensityConfig ||
    chart.visualizationSpec?.visualization?.chartType === 'imageDensity'
  ) {
    const cfg = resolveImageDensityConfig(chart, data);
    return {
      ...chart,
      type: chart.type || 'imageDensity',
      xColumn: cfg.xColumn,
      yColumn: cfg.yColumn,
      imageDensityConfig: cfg,
      advancedConfig: { ...(chart.advancedConfig || {}), imageDensityConfig: cfg },
    };
  }

  if (
    chart.type === 'imageCarousel' ||
    chart.imageCarouselConfig ||
    chart.advancedConfig?.imageCarouselConfig ||
    chart.visualizationSpec?.visualization?.chartType === 'imageCarousel'
  ) {
    const cfg = resolveImageCarouselConfig(chart, data);
    return {
      ...chart,
      type: chart.type || 'imageCarousel',
      imageCarouselConfig: cfg,
      advancedConfig: { ...(chart.advancedConfig || {}), imageCarouselConfig: cfg },
    };
  }

  if (
    chart.type === 'geoHeatmap' ||
    chart.geoHeatmapConfig ||
    chart.advancedConfig?.geoHeatmapConfig ||
    chart.visualizationSpec?.visualization?.chartType === 'geoHeatmap'
  ) {
    const cfg = resolveGeoHeatmapConfig(chart, data);
    return {
      ...chart,
      type: chart.type || 'geoHeatmap',
      latColumn: cfg.latColumn,
      lngColumn: cfg.lngColumn,
      xColumn: cfg.latColumn,
      yColumn: cfg.lngColumn,
      geoHeatmapConfig: cfg,
      advancedConfig: { ...(chart.advancedConfig || {}), geoHeatmapConfig: cfg },
    };
  }

  const vizBg = chart.visualizationSpec?.visualization?.backgroundImageUrl;
  if (!vizBg) return chart;

  if ((chart.type === 'heatmapGrid' || chart.heatmapGridConfig) && !chart.heatmapGridConfig?.backgroundImageUrl) {
    return {
      ...chart,
      heatmapGridConfig: { ...(chart.heatmapGridConfig || {}), backgroundImageUrl: vizBg },
    };
  }

  return chart;
}

export function chartTitle(chart) {
  return chart?.label || chart?.advancedConfig?.title || chart?.kpiLabel || 'Sin título';
}

export function buildLibraryEntry(chart, { visibleOnGrid = true, isReference = false } = {}) {
  if (!chart?.id) return null;
  return {
    id: chart.id,
    originalChartId: chart.originalChartId || null,
    visibleOnGrid: visibleOnGrid !== false,
    title: chartTitle(chart),
    isReference: isReference === true,
  };
}

export function normalizeDashboardLibrary(dashboard) {
  const charts = Array.isArray(dashboard?.charts) ? dashboard.charts : [];
  const savedLib = Array.isArray(dashboard?.chartLibrary) ? dashboard.chartLibrary : [];
  if (savedLib.length) return savedLib;
  return charts.map((c) => buildLibraryEntry(c, { visibleOnGrid: true, isReference: false })).filter(Boolean);
}

export function visibleChartsFromDashboard(dashboard) {
  const charts = Array.isArray(dashboard?.charts) ? dashboard.charts : [];
  const library = normalizeDashboardLibrary(dashboard);
  if (!library.length) return charts;
  const hidden = new Set(
    library.filter((e) => e.visibleOnGrid === false).map((e) => e.id)
  );
  return charts.filter((c) => !hidden.has(c.id));
}

/** Dashboard listo para grid/reporte: solo gráficos visibles y su layout. */
export function dashboardForDisplay(dashboard) {
  if (!dashboard) return dashboard;
  const visible = visibleChartsFromDashboard(dashboard);
  const visibleIds = new Set(visible.map((c) => c.id));
  const layout = Array.isArray(dashboard.layout)
    ? dashboard.layout.filter((l) => visibleIds.has(l.i))
    : [];
  return {
    ...dashboard,
    charts: visible.map((c) => enrichChartConfig(c)),
    layout,
  };
}

export function upsertLibraryEntry(library, chart, { visibleOnGrid = true, isReference } = {}) {
  const list = Array.isArray(library) ? [...library] : [];
  const existing = list.find((e) => e.id === chart?.id);
  const entry = buildLibraryEntry(chart, {
    visibleOnGrid,
    isReference: isReference !== undefined ? isReference : existing?.isReference === true,
  });
  if (!entry) return list;
  const idx = list.findIndex((e) => e.id === entry.id);
  if (idx >= 0) list[idx] = { ...list[idx], ...entry };
  else list.push(entry);
  return list;
}

export function markHiddenInLibrary(library, chartId) {
  return (library || []).map((e) =>
    e.id === chartId ? { ...e, visibleOnGrid: false } : e
  );
}

export function showChartOnGrid(library, chartId) {
  return (library || []).map((e) =>
    e.id === chartId ? { ...e, visibleOnGrid: true } : e
  );
}

export function removeFromLibrary(library, chartId) {
  return (library || []).filter((e) => e.id !== chartId);
}

export function isLibraryReference(entry) {
  return entry?.isReference === true;
}

export function isSharedOriginal(original) {
  return originalUsageCount(original) > 1;
}

export function groupOriginalsByCollection(originals) {
  const map = new Map();
  for (const o of originals || []) {
    const key = String(o?.collectionGroup || 'General').trim() || 'General';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(o);
  }
  return [...map.entries()].sort(([a], [b]) => {
    if (a === 'General') return -1;
    if (b === 'General') return 1;
    return a.localeCompare(b, 'es');
  });
}

export function originalToWizardLoad(original) {
  if (!original) return null;
  const cfg = original.chartConfig || {};
  const imageDensityConfig = cfg.imageDensityConfig || cfg.advancedConfig?.imageDensityConfig;
  const ws = original.wizardState;
  if (ws && (ws.kind === 'dataset' || ws.rows || ws.values)) {
    return {
      ...ws,
      title: original.title,
      imageDensityConfig,
      advancedConfig: cfg.advancedConfig,
      loadedExampleId: original.id,
      loadedExampleTitle: original.title,
    };
  }
  return {
    kind: 'dataset',
    title: original.title,
    chartType: cfg.type,
    visualization: cfg.visualizationSpec?.visualization || { chartType: cfg.type },
    imageDensityConfig,
    advancedConfig: cfg.advancedConfig,
    loadedExampleId: original.id,
    loadedExampleTitle: original.title,
  };
}
